'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, Calendar, Search, BookOpen, Lightbulb, Settings, Zap, Star, ArrowRight, ExternalLink, Sparkles, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/navigation'
import { toast } from "sonner"
import { useFormatDateToDisplay } from '@/lib/utils'
import Link from 'next/link'
import Image from 'next/image'

interface Page {
    id: string
    title: string
    description?: string
    slug: string
    created_at: string
    updated_at: string
}

interface MousePosition {
  x: number;
  y: number;
}

const GlassmorphismCenter: React.FC<{ className?: string }> = ({ className }) => {
  const [mousePos, setMousePos] = useState<MousePosition>({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const text = "Center";
  
  return (
      <h1 className={`select-none cursor-auto text-5xl md:text-7xl font-bold tracking-tight mb-8 text-stone-900 dark:text-stone-100 transition-colors duration-300 leading-tight ${className}`}>
        Knowledge{' '}
        <span
          ref={containerRef}
          className="relative inline-block bg-gradient-to-r from-stone-600 via-slate-600 to-stone-600 bg-clip-text text-transparent"
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {text.split('').map((char, index) => {
            const charRect = containerRef.current?.children[index]?.getBoundingClientRect();
            const containerRect = containerRef.current?.getBoundingClientRect();
            
            let distanceFromCursor = Infinity;
            if (charRect && containerRect && isHovering) {
              const charCenterX = charRect.left + charRect.width / 2 - containerRect.left;
              const charCenterY = charRect.top + charRect.height / 2 - containerRect.top;
              distanceFromCursor = Math.sqrt(
                Math.pow(mousePos.x - charCenterX, 2) + Math.pow(mousePos.y - charCenterY, 2)
              );
            }

            const maxDistance = 80;
            const intensity = Math.max(0, 1 - distanceFromCursor / maxDistance);
            
            return (
              <span
                key={index}
                className="relative inline-block transition-all duration-150 ease-out"
                style={{
                  filter: isHovering && intensity > 0 
                    ? `brightness(${1 + intensity * 0.3}) saturate(${1 + intensity * 0.2}) drop-shadow(0 0 ${intensity * 8}px rgba(255, 255, 255, ${intensity * 0.3}))` 
                    : 'none',
                  textShadow: isHovering && intensity > 0 
                    ? `
                      0 0 ${intensity * 4}px rgba(255, 255, 255, ${intensity * 0.5}),
                      0 0 ${intensity * 8}px rgba(255, 255, 255, ${intensity * 0.3}),
                      0 0 ${intensity * 12}px rgba(255, 255, 255, ${intensity * 0.2}),
                      0 0 ${intensity * 16}px rgba(255, 255, 255, ${intensity * 0.1})
                    ` 
                    : 'none',
                  transform: isHovering && intensity > 0 
                    ? `scale(${1 + intensity * 0.05}) translateY(${-intensity * 1}px)` 
                    : 'none',
                }}
              >
                {char}
              </span>
            );
          })}
          

        </span>
      </h1>
  );
};

// Add category mapping for icons
const getCategoryIcon = (slug: string) => {
    if (slug.includes('guide') || slug.includes('tutorial')) return <BookOpen className="h-5 w-5" />
    if (slug.includes('tip') || slug.includes('best-practice')) return <Lightbulb className="h-5 w-5" />
    if (slug.includes('setup') || slug.includes('config')) return <Settings className="h-5 w-5" />
    if (slug.includes('quick') || slug.includes('start')) return <Zap className="h-5 w-5" />
    return <FileText className="h-5 w-5" />
}

const getGradientClass = (index: number) => {
    const gradients = [
        'from-blue-500/20 to-purple-500/20',
        'from-emerald-500/20 to-teal-500/20',
        'from-orange-500/20 to-red-500/20',
        'from-pink-500/20 to-rose-500/20',
        'from-indigo-500/20 to-blue-500/20',
        'from-green-500/20 to-emerald-500/20'
    ]
    return gradients[index % gradients.length]
}

export default function PagesClient() {
    const { t } = useTranslation()
    const router = useRouter()
    const [pages, setPages] = useState<Page[]>([])
    const [filteredPages, setFilteredPages] = useState<Page[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const formatDateToDisplay = useFormatDateToDisplay()

    const fetchPages = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/pages`)
            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.message || 'Failed to fetch pages')
            }

            setPages(data.data || data || [])
            setFilteredPages(data.data || data || [])
        } catch (error) {
            console.error('Failed to fetch pages:', error)
            toast.error(t('common.error.title'), {
                description: 'Failed to fetch pages',
            })
        } finally {
            setIsLoading(false)
        }
    }, [t])

    useEffect(() => {
        fetchPages()
    }, [fetchPages])

    // Filter pages based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredPages(pages)
        } else {
            const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.trim() !== '');
            const filtered = pages.filter(page => {
                const pageContent = `${page.title} ${page.description || ''} ${page.slug}`.toLowerCase();
                return searchTerms.every(term => pageContent.includes(term));
            });
            setFilteredPages(filtered)
        }
    }, [searchQuery, pages])

    const handlePageClick = (slug: string) => {
        router.push(`/pages/${slug}`)
    }

    const handleBackClick = () => {
        if (window.history.length > 1) {
            router.back()
        } else {
            router.push('/')
        }
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 scroll-smooth transition-colors duration-300 relative overflow-hidden">
                {/* Natural background with subtle gradient */}
                <div className="fixed inset-0 -z-50">
                    <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-neutral-50 to-neutral-100 dark:from-stone-900 dark:via-neutral-900 dark:to-neutral-900 transition-colors duration-300" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.01),transparent_70%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.01),transparent_70%)]" />
                </div>

                {/* Navigation Header */}
                <nav className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-[95%] max-w-6xl rounded-2xl shadow-xl mx-auto backdrop-blur-3xl bg-white/40 dark:bg-neutral-900/90 border border-stone-200/50 dark:border-neutral-700/50 transition-colors duration-300">
                    <div className="flex items-center justify-between px-6 py-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-800 dark:bg-transparent text-white shadow-lg transition-colors duration-300">
                                    <Image
                                        src="/fd-logo-sidebar.png"
                                        alt="FlightDeck Logo"
                                        width={20}
                                        height={20}
                                    />
                                </div>
                                <div className="hidden sm:block">
                                    <h2 className="font-semibold text-stone-900 dark:text-stone-100 transition-colors duration-300">FlightDeck</h2>
                                    <p className="text-xs text-stone-600 dark:text-stone-400">Documentation</p>
                                </div>
                            </div>
                        </div>
                        
                        <Link href="/">
                            <Button variant="ghost" className="gap-2 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100">
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </Button>
                        </Link>
                    </div>
                </nav>

                <main className="pt-28 pb-12">
                    <div className="container mx-auto px-4">
                        <div className="mb-12 text-center">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <Skeleton className="h-8 w-8 rounded-full" />
                                <Skeleton className="h-6 w-32" />
                            </div>
                            <Skeleton className="h-12 w-96 mb-4 mx-auto" />
                            <Skeleton className="h-6 w-[600px] mb-8 mx-auto" />
                            <Skeleton className="h-12 w-80 mx-auto rounded-full" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <Card key={i} className="group relative backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${getGradientClass(i)} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                                    <CardHeader className="relative">
                                        <div className="flex items-center gap-3 mb-3">
                                            <Skeleton className="h-8 w-8 rounded-lg" />
                                            <Skeleton className="h-6 w-3/4" />
                                        </div>
                                        <Skeleton className="h-4 w-full mb-2" />
                                        <Skeleton className="h-4 w-2/3" />
                                    </CardHeader>
                                    <CardContent className="relative">
                                        <div className="flex items-center justify-between mb-4">
                                            <Skeleton className="h-4 w-24" />
                                        </div>
                                        <Skeleton className="h-8 w-20 rounded-lg" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 scroll-smooth transition-colors duration-300 relative overflow-hidden">
            {/* Natural background with subtle gradient */}
            <div className="fixed inset-0 -z-50">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-neutral-50 to-neutral-100 dark:from-stone-900 dark:via-neutral-900 dark:to-neutral-900 transition-colors duration-300" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.01),transparent_70%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.01),transparent_70%)]" />
            </div>

            {/* Navigation with glass effect */}
            <nav className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-[95%] max-w-6xl rounded-2xl shadow-xl mx-auto backdrop-blur-3xl bg-white/40 dark:bg-neutral-900/90 border border-stone-200/50 dark:border-neutral-700/50 transition-colors duration-300">
                <div className="flex items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-800 dark:bg-transparent text-white shadow-lg transition-colors duration-300">
                                <Image
                                    src="/fd-logo.svg"
                                    alt="FlightDeck Logo"
                                    width={20}
                                    height={20}
                                />
                            </div>
                            <div className="hidden sm:block">
                                <h2 className="font-semibold text-stone-900 dark:text-stone-100 transition-colors duration-300">FlightDeck</h2>
                                <p className="text-xs text-stone-600 dark:text-stone-400">Documentation</p>
                            </div>
                        </div>
                    </div>
                    
                    <Link href="/">
                        <Button variant="ghost" className="gap-2 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100">
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                    </Link>
                </div>
            </nav>

            <main className="flex-1 pt-28">
                {/* Hero Section */}
                <section className="py-12 md:py-20 relative">
                    <div className="container mx-auto px-4">
                        {/* Header Section */}
                        <div className="text-center mb-16 relative">
                            {/* Back Button */}

                            {/* Floating sparkles */}
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                <Sparkles className="h-6 w-6 text-amber-400 animate-pulse" />
                            </div>
                            <div className="absolute top-8 right-1/4 transform">
                                <Star className="h-4 w-4 text-blue-400 animate-pulse delay-500" />
                            </div>
                            <div className="absolute top-12 left-1/4 transform">
                                <Star className="h-3 w-3 text-emerald-400 animate-pulse delay-1000" />
                            </div>

                            <Badge className="group rounded-full px-6 py-3 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg mb-6 transition-all duration-300 hover:scale-105 hover:shadow-xl" variant="secondary">
                                <span className="text-sm font-medium text-stone-700 dark:text-stone-300 flex items-center gap-2">
                                    📚 <span className="group-hover:animate-pulse">Documentation Hub</span>
                                </span>
                            </Badge>

                            <GlassmorphismCenter className="md:block hidden" />
                            <h1 className="md:hidden block text-5xl font-bold tracking-tight mb-8 text-stone-900 dark:text-stone-100 transition-colors duration-300 leading-tight">
                                Knowledge <span className="relative inline-block bg-gradient-to-r from-stone-600 via-slate-600 to-stone-600 bg-clip-text text-transparent">Center</span>
                            </h1>

                            <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-400 max-w-4xl mx-auto mb-12 transition-colors duration-300 leading-relaxed">
                                Discover comprehensive guides, tutorials, and resources designed to elevate your FlightDeck experience to new heights
                            </p>

                            {/* Search Bar */}
                            <div className="relative max-w-2xl mx-auto group">
                                <div className="absolute inset-0 bg-gradient-to-r from-stone-600/20 via-slate-600/20 to-stone-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <div className="relative">
                                    <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-stone-400 dark:text-stone-500 h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
                                    <Input
                                        placeholder="Search for guides, tutorials, tips..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-16 pr-6 h-16 text-lg backdrop-blur-md bg-white/80 dark:bg-neutral-800/80 border-2 border-stone-200/50 dark:border-slate-700/50 shadow-xl hover:shadow-2xl transition-all duration-300 focus:ring-4 focus:ring-stone-500/20 dark:focus:ring-slate-400/20 focus:border-stone-400/50 dark:focus:border-slate-400/50 rounded-2xl group-hover:scale-[1.02]"
                                    />
                                    {searchQuery && (
                                        <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
                                            <Badge variant="secondary" className="bg-stone-100 dark:bg-stone-900/50 text-stone-700 dark:text-stone-300 animate-pulse">
                                                {filteredPages.length} found
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="mt-12 flex justify-center gap-8 text-sm text-stone-500 dark:text-stone-400">
                                <div className="flex items-center gap-2 group">
                                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-pulse"></div>
                                    <span className="group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors">
                                        <span className="font-bold text-stone-600 dark:text-stone-400">{pages.length}</span> articles
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 group">
                                    <div className="w-2 h-2 bg-slate-500 rounded-full animate-pulse delay-300"></div>
                                    <span className="group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors">
                                        <span className="font-bold text-slate-600 dark:text-slate-400">Always</span> updated
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 group">
                                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-pulse delay-500"></div>
                                    <span className="group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors">
                                        <span className="font-bold text-stone-600 dark:text-stone-400">100%</span> free
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Pages Grid */}
                        {filteredPages.length === 0 && !isLoading ? (
                            <div className="text-center py-20">
                                <Card className="backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-2xl max-w-lg mx-auto p-16 hover:shadow-3xl transition-all duration-500 hover:scale-105">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-stone-500/10 to-slate-500/10 rounded-full blur-3xl"></div>
                                        <FileText className="relative h-24 w-24 text-stone-400 dark:text-stone-500 mx-auto mb-8 animate-pulse" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-stone-900 dark:text-stone-100 transition-colors duration-300">No pages found</h3>
                                    <p className="text-lg text-stone-600 dark:text-stone-400 transition-colors duration-300">
                                        {searchQuery ? `No pages match "${searchQuery}"` : 'No documentation pages available'}
                                    </p>
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="mt-6 px-6 py-3 bg-stone-500 hover:bg-stone-600 text-white rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                                        >
                                            Clear search
                                        </button>
                                    )}
                                </Card>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredPages.map((page, index) => (
                                    <Card
                                        key={page.id}
                                        className="group relative cursor-pointer backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 hover:rotate-1 overflow-hidden transform-gpu perspective-1000"
                                        onClick={() => handlePageClick(page.slug)}
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        {/* Animated gradient overlay */}
                                        <div className={`absolute inset-0 bg-gradient-to-br from-stone-600/10 to-slate-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                                        {/* Floating icon */}
                                        <div className="absolute top-4 right-4 p-2 rounded-xl bg-white/80 dark:bg-neutral-700/80 backdrop-blur-sm border border-white/50 dark:border-neutral-600/50 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                                            <div className="text-stone-600 dark:text-stone-400 group-hover:text-stone-800 dark:group-hover:text-stone-200 transition-colors duration-300">
                                                {getCategoryIcon(page.slug)}
                                            </div>
                                        </div>

                                        <CardHeader className="relative pb-3 pt-6">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1">
                                                    <CardTitle className="text-xl leading-tight mb-4 line-clamp-2 text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-all duration-300 font-bold w-[85%]">
                                                        {page.title}
                                                    </CardTitle>
                                                    {page.description && (
                                                        <CardDescription className="line-clamp-3 text-base text-stone-600 dark:text-stone-400 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 leading-relaxed">
                                                            {page.description}
                                                        </CardDescription>
                                                    )}
                                                </div>
                                            </div>
                                        </CardHeader>

                                        <CardContent className="relative pt-0">
                                            <div className="flex items-center justify-between text-sm text-stone-500 dark:text-stone-400 mb-6 transition-colors duration-300">
                                                <div className="flex items-center gap-2 group-hover:text-stone-600 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                    <Calendar className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                                                    <span className="font-medium">{formatDateToDisplay(page.updated_at)}</span>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between pt-4 border-t border-stone-200/50 dark:border-stone-700/50 group-hover:border-stone-300/50 dark:group-hover:border-stone-600/50 transition-colors duration-300">
                                                <code className="text-xs bg-stone-100/80 dark:bg-stone-800/80 text-stone-700 dark:text-stone-300 px-3 py-2 rounded-lg border border-stone-200/50 dark:border-stone-700/50 transition-all duration-300 group-hover:bg-stone-200/80 dark:group-hover:bg-stone-700/80 group-hover:text-stone-800 dark:group-hover:text-stone-200 group-hover:border-stone-300/50 dark:group-hover:border-stone-600/50 font-mono">
                                                    /{page.slug}
                                                </code>

                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
                                                    <span className="text-xs font-medium text-stone-600 dark:text-stone-400">Read more</span>
                                                    <ArrowRight className="h-3 w-3 text-stone-600 dark:text-stone-400 group-hover:translate-x-1 transition-transform duration-300" />
                                                </div>
                                            </div>
                                        </CardContent>

                                        {/* Hover effect overlay */}
                                        <div className="absolute inset-0 border-2 border-transparent group-hover:border-stone-400/30 dark:group-hover:border-stone-500/30 rounded-lg transition-all duration-300 pointer-events-none"></div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    )
}